<template>
  <div class="sales-quote-page">
    <a-alert
      type="info"
      show-icon
      message="假数据预览。点选和报价只留在本页，不会写入服务器。"
      style="margin-bottom: 16px"
    />

    <div class="quote-section">
      <div class="section-title">报价信息</div>
      <a-form
        class="simiot-compact-form"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-row>
          <a-col :span="12">
            <a-form-item label="客户名称">
              <a-input
                v-model="quote.customerName"
                allow-clear
                placeholder="可不填"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="有效期">
              <a-input-number
                v-model="quote.validDays"
                :min="1"
                :precision="0"
                style="width: 120px"
              />
              <span class="unit-text">天</span>
            </a-form-item>
          </a-col>
        </a-row>
        <a-row>
          <a-col :span="12">
            <a-form-item label="备注">
              <a-input
                v-model="quote.remark"
                allow-clear
                placeholder="选填"
              />
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </div>

    <a-divider />

    <div class="quote-section">
      <div class="section-title">添加一行</div>
      <div class="hint section-hint">同一张报价单可以连续添加多行。换产品类型后重新点选，再点「添加本行」，SIM、eSIM、Profile、模组、CPE 可以混在一张单里。</div>
      <a-row :gutter="24">
        <a-col :span="12">
          <a-form
            class="simiot-compact-form"
            :label-col="{ span: 6 }"
            :wrapper-col="{ span: 18 }"
          >
            <a-form-item label="产品类型">
              <a-radio-group
                button-style="solid"
                :value="draft.productType"
                @change="changeProductType"
              >
                <a-radio-button
                  v-for="item in productTypes"
                  :key="item.value"
                  class="card-select-box"
                  :value="item.value"
                >
                  {{ item.label }}
                </a-radio-button>
              </a-radio-group>
            </a-form-item>

            <template v-if="draft.productType === 'sim'">
              <a-form-item label="套餐类型">
                <a-radio-group
                  button-style="solid"
                  :value="draft.packageKind"
                  @change="changePackageKind"
                >
                  <a-radio-button
                    v-for="item in packageKinds"
                    :key="item.value"
                    class="card-select-box"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </a-radio-button>
                </a-radio-group>
              </a-form-item>

              <template v-if="draft.packageKind === 'common'">
                <a-form-item label="运营商">
                  <a-select
                    :value="draft.carrier"
                    placeholder="请选择"
                    @change="changeCarrier"
                  >
                    <a-select-option
                      v-for="item in carrierOptions"
                      :key="item"
                      :value="item"
                    >
                      {{ item }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
                <a-form-item label="流量场景">
                  <a-select
                    :value="draft.scene"
                    placeholder="请选择"
                    :disabled="!draft.carrier"
                    @change="changeScene"
                  >
                    <a-select-option
                      v-for="item in sceneOptions"
                      :key="item"
                      :value="item"
                    >
                      {{ item }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
                <a-form-item label="套餐大小">
                  <a-radio-group
                    button-style="solid"
                    :value="draft.size"
                    @change="changeSize"
                  >
                    <a-radio-button
                      v-for="item in sizeOptions"
                      :key="item"
                      class="card-select-box"
                      :value="item"
                    >
                      {{ item }}
                    </a-radio-button>
                  </a-radio-group>
                  <div v-if="draft.scene && sizeOptions.length === 0" class="hint">该场景还没有上架套餐</div>
                </a-form-item>
                <a-form-item v-if="needsVoiceChoice" label="语音">
                  <a-radio-group
                    button-style="solid"
                    :value="draft.voice"
                    @change="changeVoice"
                  >
                    <a-radio-button
                      v-for="item in voiceOptions"
                      :key="item || 'none'"
                      class="card-select-box"
                      :value="item"
                    >
                      {{ item || '无语音' }}
                    </a-radio-button>
                  </a-radio-group>
                </a-form-item>
                <a-form-item v-if="matchedProducts.length > 1" label="上架套餐">
                  <a-select
                    :value="draft.productId"
                    placeholder="请选择"
                    @change="changeProduct"
                  >
                    <a-select-option
                      v-for="item in matchedProducts"
                      :key="item.id"
                      :value="item.id"
                    >
                      {{ item.name }}（{{ item.productKind }} / {{ item.business }} / {{ item.trafficProperty }}）
                    </a-select-option>
                  </a-select>
                </a-form-item>
                <a-form-item v-if="selectedProduct && matchedProducts.length === 1" label="上架套餐">
                  <span>{{ selectedProduct.name }}（{{ selectedProduct.productKind }} / {{ selectedProduct.business }} / {{ selectedProduct.trafficProperty }}）</span>
                </a-form-item>
                <a-form-item label="卡板材质">
                  <a-radio-group
                    button-style="solid"
                    :value="draft.material"
                    @change="changeMaterial"
                  >
                    <a-radio-button
                      v-for="item in materialOptions"
                      :key="item.name"
                      class="card-select-box card-type-box"
                      :value="item.name"
                    >
                      {{ item.name }}
                    </a-radio-button>
                  </a-radio-group>
                </a-form-item>
                <a-form-item label="卡片尺寸">
                  <a-radio-group
                    button-style="solid"
                    :value="draft.shape"
                    @change="changeShape"
                  >
                    <a-radio-button
                      v-for="item in shapeOptions"
                      :key="item"
                      class="card-select-box card-shape-box"
                      :value="item"
                    >
                      {{ item }}
                    </a-radio-button>
                  </a-radio-group>
                </a-form-item>
                <a-form-item label="订购周期">
                  <a-radio-group
                    button-style="solid"
                    :value="draft.orderCycles"
                    @change="changeOrderCycles"
                  >
                    <a-radio-button
                      v-for="item in orderCycleOptions"
                      :key="item"
                      class="card-select-box"
                      :value="item"
                    >
                      {{ item }}个周期
                    </a-radio-button>
                  </a-radio-group>
                </a-form-item>
              </template>

              <template v-else-if="draft.packageKind === 'pool'">
                <a-form-item label="总包类型">
                  <a-radio-group
                    button-style="solid"
                    :value="draft.poolType"
                    @change="changePoolType"
                  >
                    <a-radio-button class="card-select-box" value="single">单一运营商</a-radio-button>
                    <a-radio-button class="card-select-box" value="multi">融合运营商</a-radio-button>
                  </a-radio-group>
                </a-form-item>
                <a-form-item label="总包容量">
                  <a-radio-group
                    button-style="solid"
                    :value="draft.size"
                    @change="changeCatalogSize"
                  >
                    <a-radio-button
                      v-for="item in poolSizeOptions"
                      :key="item"
                      class="card-select-box"
                      :value="item"
                    >
                      {{ item }}
                    </a-radio-button>
                  </a-radio-group>
                </a-form-item>
                <a-form-item v-if="matchedProducts.length > 1" label="上架总包">
                  <a-select
                    :value="draft.productId"
                    placeholder="请选择"
                    @change="changeProduct"
                  >
                    <a-select-option
                      v-for="item in matchedProducts"
                      :key="item.id"
                      :value="item.id"
                    >
                      {{ item.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
                <a-form-item v-if="selectedProduct && matchedProducts.length === 1" label="上架总包">
                  <span>{{ selectedProduct.name }}</span>
                </a-form-item>
                <a-form-item label="订购周期">
                  <a-radio-group
                    button-style="solid"
                    :value="draft.orderCycles"
                    @change="changeOrderCycles"
                  >
                    <a-radio-button
                      v-for="item in orderCycleOptions"
                      :key="item"
                      class="card-select-box"
                      :value="item"
                    >
                      {{ item }}个周期
                    </a-radio-button>
                  </a-radio-group>
                </a-form-item>
              </template>

              <template v-else-if="draft.packageKind === 'addon'">
                <a-form-item label="加油包类型">
                  <a-radio-group
                    button-style="solid"
                    :value="draft.addonType"
                    @change="changeAddonType"
                  >
                    <a-radio-button class="card-select-box" value="flow">流量包</a-radio-button>
                    <a-radio-button class="card-select-box" value="voice">语音包</a-radio-button>
                  </a-radio-group>
                </a-form-item>
                <a-form-item :label="draft.addonType === 'voice' ? '语音时长' : '加油包容量'">
                  <a-radio-group
                    button-style="solid"
                    :value="draft.size"
                    @change="changeCatalogSize"
                  >
                    <a-radio-button
                      v-for="item in addonSizeOptions"
                      :key="item"
                      class="card-select-box"
                      :value="item"
                    >
                      {{ item }}
                    </a-radio-button>
                  </a-radio-group>
                </a-form-item>
                <a-form-item v-if="matchedProducts.length > 1" label="上架加油包">
                  <a-select
                    :value="draft.productId"
                    placeholder="请选择"
                    @change="changeProduct"
                  >
                    <a-select-option
                      v-for="item in matchedProducts"
                      :key="item.id"
                      :value="item.id"
                    >
                      {{ item.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
                <a-form-item v-if="selectedProduct && matchedProducts.length === 1" label="上架加油包">
                  <span>{{ selectedProduct.name }}</span>
                </a-form-item>
                <a-form-item label="订购周期">
                  <a-radio-group
                    button-style="solid"
                    :value="draft.orderCycles"
                    @change="changeOrderCycles"
                  >
                    <a-radio-button
                      v-for="item in orderCycleOptions"
                      :key="item"
                      class="card-select-box"
                      :value="item"
                    >
                      {{ item }}个周期
                    </a-radio-button>
                  </a-radio-group>
                </a-form-item>
              </template>
            </template>

            <template v-else-if="draft.productType === 'esim'">
              <a-form-item label="上架eSIM">
                <a-select
                  :value="draft.goodsId"
                  placeholder="请选择"
                  @change="changeGoods"
                >
                  <a-select-option
                    v-for="item in esimGoods"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ item.name }}
                  </a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item label="卡片材质">
                <a-radio-group
                  button-style="solid"
                  :value="draft.material"
                  @change="changeSimpleMaterial"
                >
                  <a-radio-button
                    v-for="item in simpleMaterials"
                    :key="item.name"
                    class="card-select-box card-type-box"
                    :value="item.name"
                  >
                    {{ item.name }}
                  </a-radio-button>
                </a-radio-group>
              </a-form-item>
              <a-form-item label="卡片尺寸">
                <a-radio-group
                  button-style="solid"
                  :value="draft.shape"
                  @change="changeShape"
                >
                  <a-radio-button
                    v-for="item in simpleShapes"
                    :key="item"
                    class="card-select-box card-shape-box"
                    :value="item"
                  >
                    {{ item }}
                  </a-radio-button>
                </a-radio-group>
              </a-form-item>
            </template>

            <template v-else-if="draft.productType === 'profile'">
              <a-form-item label="上架Profile">
                <a-select
                  :value="draft.goodsId"
                  placeholder="请选择"
                  @change="changeGoods"
                >
                  <a-select-option
                    v-for="item in profileGoods"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ item.name }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </template>

            <template v-else-if="draft.productType === 'module'">
              <a-form-item label="模组厂商">
                <a-select
                  :value="draft.producer"
                  placeholder="请选择"
                  @change="changeProducer"
                >
                  <a-select-option
                    v-for="item in producerOptions"
                    :key="item"
                    :value="item"
                  >
                    {{ item }}
                  </a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item label="模组型号">
                <a-select
                  :value="draft.goodsId"
                  placeholder="请选择"
                  :disabled="!draft.producer"
                  @change="changeGoods"
                >
                  <a-select-option
                    v-for="item in moduleOptions"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ item.model }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </template>

            <template v-else>
              <a-form-item label="CPE型号">
                <a-select
                  :value="draft.goodsId"
                  placeholder="请选择"
                  @change="changeGoods"
                >
                  <a-select-option
                    v-for="item in cpeGoods"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ item.model }} / {{ item.comm }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </template>

            <a-form-item label="数量">
              <a-input-number
                v-model="draft.quantity"
                :min="1"
                :precision="0"
                style="width: 120px"
              />
              <span class="unit-text">{{ quantityUnit }}</span>
            </a-form-item>
          </a-form>
        </a-col>

        <a-col :span="12">
          <div class="section-title">报价与库存</div>
          <div v-if="!priceReady" class="hint">完成左侧点选后显示报价</div>
          <template v-else>
            <a-table
              size="middle"
              :columns="priceColumns"
              :data-source="priceRows"
              :pagination="false"
              row-key="key"
            >
              <template slot="quote" slot-scope="text, record">
                <a-input-number
                  :value="record.quote"
                  :min="0"
                  :step="0.001"
                  style="width: 110px"
                  @change="value => changeQuote(record.key, value)"
                />
              </template>
            </a-table>
            <div class="stock-box">
              <div v-if="stock.tracked">
                库存 {{ stock.count }} {{ quantityUnit }}
                <a-tag v-if="stock.count === 0" color="orange">库存不足</a-tag>
              </div>
              <div v-if="stock.note" class="hint">{{ stock.note }}</div>
              <div v-for="item in stock.supplies" :key="item" class="hint">{{ item }}</div>
            </div>
            <div class="add-line-action">
              <a-button type="primary" @click="addLine">添加本行</a-button>
            </div>
          </template>
        </a-col>
      </a-row>
    </div>

    <a-divider />

    <div class="quote-section">
      <div class="section-title">报价明细</div>
      <a-table
        size="middle"
        :columns="lineColumns"
        :data-source="lines"
        :pagination="false"
        row-key="id"
      >
        <template slot="belowFloor" slot-scope="text">
          <a-tag v-if="text" color="red">低于底价</a-tag>
          <span v-else>正常</span>
        </template>
        <template slot="action" slot-scope="text, record">
          <a @click="removeLine(record.id)">移除</a>
        </template>
      </a-table>
      <div class="quote-footer">
        <div>货款合计 {{ money(goodsTotal) }} 元</div>
        <a-space>
          <a-button :disabled="lines.length === 0" @click="copyCustomerQuote">复制客户报价</a-button>
          <a-button type="primary" :disabled="lines.length === 0" @click="saveQuote">保存</a-button>
        </a-space>
      </div>
    </div>
  </div>
</template>

<script>
const simProducts = [
  {
    id: 1,
    name: '移动车联网通用',
    carrier: '移动',
    scene: '车联网',
    size: '30GB',
    productKind: '通用套餐',
    business: '自定义自然月',
    trafficProperty: '通用',
    tiers: {
      1: { standard: 8, floor: 5 },
      12: { standard: 36, floor: 22 }
    },
    cardTypes: [
      { name: '插拔卡', standard: 3, floor: 1.5, shapes: ['大卡', '中卡', '小卡'] },
      { name: '贴片卡', standard: 4, floor: 2, shapes: ['5*6', '2*2'] }
    ],
    excess: { standard: 0.29, floor: 0.15, unit: '元/MB' },
    stop: { standard: 1, floor: 0.5, unit: '元/月' }
  },
  {
    id: 2,
    name: '移动车联网定向',
    carrier: '移动',
    scene: '车联网',
    size: '30GB',
    productKind: '通用套餐',
    business: '自定义自然月',
    trafficProperty: '定向',
    tiers: {
      1: { standard: 6, floor: 4 },
      12: { standard: 28, floor: 18 }
    },
    cardTypes: [
      { name: '插拔卡', standard: 3, floor: 1.5, shapes: ['大卡', '中卡'] }
    ],
    excess: { standard: 0.2, floor: 0.1, unit: '元/MB' },
    stop: { standard: 1, floor: 0.5, unit: '元/月' }
  },
  {
    id: 3,
    name: '移动车联网小流量',
    carrier: '移动',
    scene: '车联网',
    size: '100MB',
    productKind: '通用套餐',
    business: '自定义自然月',
    trafficProperty: '通用',
    tiers: {
      1: { standard: 3, floor: 1.8 },
      6: { standard: 15, floor: 9 }
    },
    cardTypes: [
      { name: '插拔卡', standard: 2, floor: 1, shapes: ['大卡', '中卡'] },
      { name: '贴片卡', standard: 2.5, floor: 1.2, shapes: ['5*6'] }
    ],
    excess: { standard: 0.29, floor: 0.15, unit: '元/MB' },
    stop: { standard: 1, floor: 0.5, unit: '元/月' }
  },
  {
    id: 4,
    name: '移动个人实名',
    carrier: '移动',
    scene: '个人实名',
    size: '2GB',
    productKind: '通用套餐',
    business: '自定义自然月',
    trafficProperty: '通用',
    tiers: {
      12: { standard: 48, floor: 30 }
    },
    cardTypes: [
      { name: '插拔卡', standard: 5, floor: 3, shapes: ['大卡'] }
    ],
    excess: { standard: 0.29, floor: 0.2, unit: '元/MB' },
    stop: { standard: 2, floor: 1, unit: '元/月' }
  },
  {
    id: 5,
    name: '联通行业通用',
    carrier: '联通',
    scene: '行业应用',
    size: '1GB',
    productKind: '通用套餐',
    business: '流量共享',
    trafficProperty: '通用',
    tiers: {
      1: { standard: 10, floor: 6 },
      12: { standard: 40, floor: 26 }
    },
    cardTypes: [
      { name: '插拔卡', standard: 3, floor: 1.5, shapes: ['大卡', '中卡'] }
    ],
    excess: { standard: 0.25, floor: 0.12, unit: '元/MB' },
    stop: { standard: 1, floor: 0.5, unit: '元/月' }
  },
  {
    id: 6,
    name: '移动车联网语音',
    carrier: '移动',
    scene: '车联网',
    size: '30GB',
    voice: '100分钟',
    productKind: '通用套餐',
    business: '自定义自然月',
    trafficProperty: '通用',
    tiers: {
      1: { standard: 12, floor: 8 },
      12: { standard: 48, floor: 32 }
    },
    cardTypes: [
      { name: '插拔卡', standard: 3, floor: 1.5, shapes: ['大卡', '中卡'] }
    ],
    excess: { standard: 0.29, floor: 0.15, unit: '元/MB' },
    stop: { standard: 1, floor: 0.5, unit: '元/月' }
  }
]

const simStocks = [
  { carrier: '移动', scene: '车联网', material: '插拔卡', shape: '大卡', count: 1260, supplies: ['移动物联网账号A / 30MB月包', '移动物联网账号A / 100MB月包'] },
  { carrier: '移动', scene: '车联网', material: '插拔卡', shape: '中卡', count: 0, supplies: ['移动物联网账号B / 30MB月包'] },
  { carrier: '移动', scene: '车联网', material: '插拔卡', shape: '小卡', count: 80, supplies: ['移动物联网账号A / 100MB月包'] },
  { carrier: '移动', scene: '车联网', material: '贴片卡', shape: '5*6', count: 540, supplies: ['移动物联网账号A / 30MB月包'] },
  { carrier: '移动', scene: '车联网', material: '贴片卡', shape: '2*2', count: 20, supplies: ['移动物联网账号C / 10MB月包'] },
  { carrier: '移动', scene: '个人实名', material: '插拔卡', shape: '大卡', count: 300, supplies: ['移动实名账号 / 1GB年包'] },
  { carrier: '联通', scene: '行业应用', material: '插拔卡', shape: '大卡', count: 90, supplies: ['联通行账账号 / 500MB月包'] },
  { carrier: '联通', scene: '行业应用', material: '插拔卡', shape: '中卡', count: 40, supplies: ['联通行账账号 / 500MB月包'] },
  { carrier: '移动', scene: '车联网', material: '插拔卡', shape: '中卡', voice: true, count: 36, supplies: ['移动语音账号 / 30MB月包'] },
  { carrier: '移动', scene: '车联网', material: '插拔卡', shape: '大卡', voice: true, count: 18, supplies: ['移动语音账号 / 30MB月包'] }
]

const poolProducts = [
  { id: 'pool-1', poolType: 'single', name: '移动100GB总包', size: '100GB', tiers: { 1: { standard: 800, floor: 600 }, 12: { standard: 7200, floor: 5400 }}},
  { id: 'pool-2', poolType: 'single', name: '移动500GB总包', size: '500GB', tiers: { 1: { standard: 3200, floor: 2500 }}},
  { id: 'pool-3', poolType: 'multi', name: '融合200GB总包', size: '200GB', tiers: { 1: { standard: 1800, floor: 1400 }, 12: { standard: 16800, floor: 13000 }}}
]

const addonProducts = [
  { id: 'addon-flow-1', addonType: 'flow', name: '1GB流量加油包', size: '1GB', tiers: { 1: { standard: 8, floor: 5 }}},
  { id: 'addon-flow-2', addonType: 'flow', name: '5GB流量加油包', size: '5GB', tiers: { 1: { standard: 30, floor: 20 }}},
  { id: 'addon-voice-1', addonType: 'voice', name: '100分钟语音加油包', size: '100分钟', tiers: { 1: { standard: 10, floor: 6 }}},
  { id: 'addon-voice-2', addonType: 'voice', name: '300分钟语音加油包', size: '300分钟', tiers: { 1: { standard: 25, floor: 16 }}}
]

const esimGoods = [
  {
    id: 'esim-1',
    name: '消费级eSIM',
    cardTypes: [
      { name: '贴片卡', standard: 15, floor: 8, shapes: ['5*6', '2*2'] }
    ],
    stocks: [
      { material: '贴片卡', shape: '5*6', count: 800, supplies: ['eSIM供应商甲'] },
      { material: '贴片卡', shape: '2*2', count: 0, supplies: ['eSIM供应商甲'] }
    ]
  }
]

const profileGoods = [
  { id: 'profile-1', name: '消费级Profile', standard: 6, floor: 3, stock: 2400, supplier: 'Profile供应商甲' },
  { id: 'profile-2', name: '工业级Profile', standard: 9, floor: 5, stock: 160, supplier: 'Profile供应商乙' }
]

const moduleGoods = [
  { id: 'module-1', producer: '移远', model: 'EC200A', standard: 45, floor: 32, stock: 180 },
  { id: 'module-2', producer: '移远', model: 'EC600', standard: 68, floor: 50, stock: 0 },
  { id: 'module-3', producer: '广和通', model: 'L610', standard: 42, floor: 30, stock: 75 }
]

const cpeGoods = [
  { id: 'cpe-1', model: 'CPE-5G-01', comm: '5G', standard: 280, floor: 210, stock: 24 },
  { id: 'cpe-2', model: 'CPE-4G-02', comm: '4G', standard: 160, floor: 120, stock: 0 }
]

function emptyDraft(productType) {
  return {
    productType: productType || 'sim',
    packageKind: 'common',
    poolType: undefined,
    addonType: undefined,
    voice: undefined,
    carrier: undefined,
    scene: undefined,
    size: undefined,
    productId: undefined,
    material: undefined,
    shape: undefined,
    orderCycles: undefined,
    goodsId: undefined,
    producer: undefined,
    quantity: 1,
    packageQuote: undefined,
    cardQuote: undefined,
    excessQuote: undefined,
    stopQuote: undefined,
    unitQuote: undefined
  }
}

function unique(list) {
  return list.filter((item, index) => list.indexOf(item) === index)
}

export default {
  name: 'SalesQuotesPreview',
  data() {
    return {
      productTypes: [
        { value: 'sim', label: 'SIM卡' },
        { value: 'esim', label: 'eSIM' },
        { value: 'profile', label: 'Profile' },
        { value: 'module', label: '模组' },
        { value: 'cpe', label: 'CPE' }
      ],
      packageKinds: [
        { value: 'common', label: '通用套餐' },
        { value: 'pool', label: '流量总池包' },
        { value: 'addon', label: '加油包' }
      ],
      esimGoods,
      profileGoods,
      quote: {
        customerName: '',
        validDays: 15,
        remark: ''
      },
      draft: emptyDraft('sim'),
      lines: [],
      nextLineId: 1,
      priceColumns: [
        { title: '项目', dataIndex: 'name' },
        { title: '标准价', dataIndex: 'standard' },
        { title: '底价', dataIndex: 'floor' },
        { title: '对外报价', dataIndex: 'quote', scopedSlots: { customRender: 'quote' }}
      ],
      lineColumns: [
        { title: '产品', dataIndex: 'summary' },
        { title: '数量', dataIndex: 'quantity' },
        { title: '对外报价', dataIndex: 'quoteText' },
        { title: '货款小计', dataIndex: 'goodsAmountText' },
        { title: '底价', dataIndex: 'belowFloor', scopedSlots: { customRender: 'belowFloor' }},
        { title: '库存', dataIndex: 'stockText' },
        { title: '操作', dataIndex: 'action', scopedSlots: { customRender: 'action' }}
      ]
    }
  },
  computed: {
    carrierOptions() {
      return unique(simProducts.map(item => item.carrier))
    },
    sceneOptions() {
      return unique(simProducts.filter(item => item.carrier === this.draft.carrier).map(item => item.scene))
    },
    sizeOptions() {
      return unique(this.sceneProducts.map(item => item.size))
    },
    voiceOptions() {
      return unique(this.sceneProducts.filter(item => item.size === this.draft.size).map(item => item.voice || ''))
    },
    needsVoiceChoice() {
      return this.voiceOptions.length > 1
    },
    poolSizeOptions() {
      return unique(poolProducts.filter(item => item.poolType === this.draft.poolType).map(item => item.size))
    },
    addonSizeOptions() {
      return unique(addonProducts.filter(item => item.addonType === this.draft.addonType).map(item => item.size))
    },
    sceneProducts() {
      return simProducts.filter(item => item.carrier === this.draft.carrier && item.scene === this.draft.scene)
    },
    catalogProducts() {
      if (this.draft.packageKind === 'pool') {
        return poolProducts.filter(item => item.poolType === this.draft.poolType && item.size === this.draft.size)
      }
      if (this.draft.packageKind === 'addon') {
        return addonProducts.filter(item => item.addonType === this.draft.addonType && item.size === this.draft.size)
      }
      if (this.needsVoiceChoice && this.draft.voice === undefined) {
        return []
      }
      return this.sceneProducts.filter(item => item.size === this.draft.size && (item.voice || '') === (this.draft.voice || ''))
    },
    matchedProducts() {
      return this.catalogProducts
    },
    selectedProduct() {
      if (this.draft.packageKind === 'pool') {
        return poolProducts.find(item => item.id === this.draft.productId)
      }
      if (this.draft.packageKind === 'addon') {
        return addonProducts.find(item => item.id === this.draft.productId)
      }
      return simProducts.find(item => item.id === this.draft.productId)
    },
    materialOptions() {
      return this.selectedProduct ? this.selectedProduct.cardTypes : []
    },
    selectedMaterial() {
      return this.materialOptions.find(item => item.name === this.draft.material)
    },
    shapeOptions() {
      return this.selectedMaterial ? this.selectedMaterial.shapes : []
    },
    orderCycleOptions() {
      if (!this.selectedProduct) {
        return []
      }
      return Object.keys(this.selectedProduct.tiers).map(item => Number(item))
    },
    selectedTier() {
      if (!this.selectedProduct || !this.draft.orderCycles) {
        return null
      }
      return this.selectedProduct.tiers[this.draft.orderCycles]
    },
    currentGoods() {
      if (this.draft.productType === 'esim') {
        return esimGoods.find(item => item.id === this.draft.goodsId)
      }
      if (this.draft.productType === 'profile') {
        return profileGoods.find(item => item.id === this.draft.goodsId)
      }
      if (this.draft.productType === 'module') {
        return moduleGoods.find(item => item.id === this.draft.goodsId)
      }
      if (this.draft.productType === 'cpe') {
        return cpeGoods.find(item => item.id === this.draft.goodsId)
      }
      return null
    },
    simpleMaterials() {
      return this.currentGoods && this.currentGoods.cardTypes ? this.currentGoods.cardTypes : []
    },
    simpleShapes() {
      const material = this.simpleMaterials.find(item => item.name === this.draft.material)
      return material ? material.shapes : []
    },
    producerOptions() {
      return unique(moduleGoods.map(item => item.producer))
    },
    moduleOptions() {
      return moduleGoods.filter(item => item.producer === this.draft.producer)
    },
    cpeGoods() {
      return cpeGoods
    },
    quantityUnit() {
      if (this.draft.productType === 'cpe') {
        return '台'
      }
      if (this.draft.productType === 'sim' && this.draft.packageKind !== 'common') {
        return '个'
      }
      if (this.draft.productType === 'profile') {
        return '个'
      }
      return '张'
    },
    priceReady() {
      if (this.draft.productType === 'sim' && this.draft.packageKind === 'common') {
        return !!(this.selectedTier && this.selectedMaterial && this.draft.shape)
      }
      if (this.draft.productType === 'sim') {
        return !!this.selectedTier
      }
      if (this.draft.productType === 'esim') {
        return !!(this.currentGoods && this.draft.material && this.draft.shape)
      }
      return !!this.currentGoods
    },
    priceRows() {
      if (!this.priceReady) {
        return []
      }
      if (this.draft.productType === 'sim' && this.draft.packageKind === 'common') {
        return [
          { key: 'packageQuote', name: '套餐费', standard: this.money(this.selectedTier.standard), floor: this.selectedTier.floor, quote: this.draft.packageQuote },
          { key: 'cardQuote', name: '卡板费', standard: this.money(this.selectedMaterial.standard), floor: this.selectedMaterial.floor, quote: this.draft.cardQuote },
          { key: 'excessQuote', name: `超额资费（${this.selectedProduct.excess.unit}）`, standard: this.money(this.selectedProduct.excess.standard), floor: this.selectedProduct.excess.floor, quote: this.draft.excessQuote },
          { key: 'stopQuote', name: `停机保号（${this.selectedProduct.stop.unit}）`, standard: this.money(this.selectedProduct.stop.standard), floor: this.selectedProduct.stop.floor, quote: this.draft.stopQuote }
        ]
      }
      if (this.draft.productType === 'sim') {
        return [
          { key: 'packageQuote', name: '套餐费', standard: this.money(this.selectedTier.standard), floor: this.selectedTier.floor, quote: this.draft.packageQuote }
        ]
      }
      const price = this.simpleUnitPrice
      return [
        { key: 'unitQuote', name: '单价', standard: this.money(price.standard), floor: price.floor, quote: this.draft.unitQuote }
      ]
    },
    simpleUnitPrice() {
      if (this.draft.productType === 'esim') {
        return this.simpleMaterials.find(item => item.name === this.draft.material) || {}
      }
      return this.currentGoods || {}
    },
    stock() {
      if (this.draft.productType === 'sim' && this.draft.packageKind === 'pool') {
        return { tracked: false, count: 0, supplies: [], note: '流量总池包按上架总包容量报价，不核卡库存。' }
      }
      if (this.draft.productType === 'sim' && this.draft.packageKind === 'addon') {
        return { tracked: false, count: 0, supplies: [], note: '加油包加在已有卡上，不核卡库存。' }
      }
      if (this.draft.productType === 'sim') {
        const voice = !!(this.selectedProduct && this.selectedProduct.voice)
        const found = simStocks.find(item => item.carrier === this.draft.carrier && item.scene === this.draft.scene && item.material === this.draft.material && item.shape === this.draft.shape && !!item.voice === voice)
        return {
          tracked: true,
          count: found ? found.count : 0,
          supplies: found ? found.supplies : [],
          note: voice ? '库存只统计该场景下的语音卡，按卡板材质、卡片尺寸分开。' : '库存按流量场景、卡板材质、卡片尺寸统计非语音卡，不按基础套餐容量折算销售套餐大小。'
        }
      }
      if (this.draft.productType === 'esim') {
        const goods = this.currentGoods
        const found = goods && goods.stocks.find(item => item.material === this.draft.material && item.shape === this.draft.shape)
        return { tracked: true, count: found ? found.count : 0, supplies: found ? found.supplies : [], note: '' }
      }
      if (this.draft.productType === 'profile') {
        return { tracked: true, count: this.currentGoods.stock, supplies: [this.currentGoods.supplier], note: '' }
      }
      if (this.currentGoods) {
        return { tracked: true, count: this.currentGoods.stock, supplies: [], note: '' }
      }
      return { tracked: true, count: 0, supplies: [], note: '' }
    },
    goodsTotal() {
      return this.lines.reduce((sum, item) => sum + item.goodsAmount, 0)
    }
  },
  methods: {
    money(value) {
      if (value === undefined || value === null || value === '') {
        return ''
      }
      return Number(value).toFixed(3).replace(/\.?0+$/, '')
    },
    changeProductType(event) {
      const productType = event.target.value
      const next = emptyDraft(productType)
      next.quantity = this.draft.quantity
      this.draft = next
    },
    changeCarrier(value) {
      this.draft.carrier = value
      this.clearFrom('scene')
    },
    changeScene(value) {
      this.draft.scene = value
      this.clearFrom('size')
    },
    changePackageKind(event) {
      const quantity = this.draft.quantity
      this.draft = emptyDraft('sim')
      this.draft.packageKind = event.target.value
      this.draft.quantity = quantity
    },
    changePoolType(event) {
      this.draft.poolType = event.target.value
      this.draft.size = undefined
      this.clearFrom('product')
    },
    changeAddonType(event) {
      this.draft.addonType = event.target.value
      this.draft.size = undefined
      this.clearFrom('product')
    },
    changeCatalogSize(event) {
      this.draft.size = event.target.value
      this.clearFrom('product')
      if (this.matchedProducts.length === 1) {
        this.applyProduct(this.matchedProducts[0].id)
      }
    },
    changeSize(event) {
      this.draft.size = event.target.value
      this.draft.voice = undefined
      this.clearFrom('product')
      if (!this.needsVoiceChoice) {
        this.draft.voice = this.voiceOptions[0] || ''
        if (this.matchedProducts.length === 1) {
          this.applyProduct(this.matchedProducts[0].id)
        }
      }
    },
    changeVoice(event) {
      this.draft.voice = event.target.value
      this.clearFrom('product')
      if (this.matchedProducts.length === 1) {
        this.applyProduct(this.matchedProducts[0].id)
      }
    },
    changeProduct(value) {
      this.applyProduct(value)
    },
    applyProduct(productId) {
      this.draft.productId = productId
      this.draft.material = undefined
      this.draft.shape = undefined
      this.draft.orderCycles = undefined
      this.draft.packageQuote = undefined
      this.draft.cardQuote = undefined
      this.draft.excessQuote = undefined
      this.draft.stopQuote = undefined
    },
    changeMaterial(event) {
      this.draft.material = event.target.value
      this.draft.shape = undefined
      this.draft.cardQuote = undefined
    },
    changeSimpleMaterial(event) {
      this.draft.material = event.target.value
      this.draft.shape = undefined
      this.draft.unitQuote = undefined
    },
    changeShape(event) {
      this.draft.shape = event.target.value
      this.fillQuotes()
    },
    changeOrderCycles(event) {
      this.draft.orderCycles = Number(event.target.value)
      this.fillQuotes()
    },
    changeGoods(value) {
      this.draft.goodsId = value
      this.draft.material = undefined
      this.draft.shape = undefined
      this.draft.unitQuote = undefined
      if (this.draft.productType === 'profile' || this.draft.productType === 'module' || this.draft.productType === 'cpe') {
        this.fillQuotes()
      }
    },
    changeProducer(value) {
      this.draft.producer = value
      this.draft.goodsId = undefined
      this.draft.unitQuote = undefined
    },
    clearFrom(field) {
      const order = ['scene', 'size', 'voice', 'product', 'material', 'shape', 'orderCycles']
      const index = order.indexOf(field)
      order.slice(index).forEach((key) => {
        if (key === 'product') {
          this.draft.productId = undefined
        } else {
          this.draft[key] = undefined
        }
      })
      this.draft.packageQuote = undefined
      this.draft.cardQuote = undefined
      this.draft.excessQuote = undefined
      this.draft.stopQuote = undefined
    },
    fillQuotes() {
      if (this.draft.productType === 'sim' && this.draft.packageKind !== 'common' && this.selectedTier) {
        this.draft.packageQuote = this.selectedTier.standard
        return
      }
      if (this.draft.productType === 'sim' && this.selectedTier && this.selectedMaterial) {
        this.draft.packageQuote = this.selectedTier.standard
        this.draft.cardQuote = this.selectedMaterial.standard
        this.draft.excessQuote = this.selectedProduct.excess.standard
        this.draft.stopQuote = this.selectedProduct.stop.standard
        return
      }
      if (this.priceReady && this.simpleUnitPrice.standard !== undefined) {
        this.draft.unitQuote = this.simpleUnitPrice.standard
      }
    },
    changeQuote(key, value) {
      this.draft[key] = value
    },
    addLine() {
      if (!this.priceReady || !this.draft.quantity) {
        this.$message.warning('请先完成点选并填写数量')
        return
      }
      if (this.isBelowFloor()) {
        this.$confirm({
          title: '对外报价低于底价',
          content: '低于底价的项目会记在内部报价上，客户可见报价里不会出现。确认加入？',
          onOk: () => this.pushLine(true)
        })
        return
      }
      this.pushLine(false)
    },
    isBelowFloor() {
      if (this.draft.productType === 'sim' && this.draft.packageKind !== 'common') {
        return this.draft.packageQuote < this.selectedTier.floor
      }
      if (this.draft.productType === 'sim') {
        return this.draft.packageQuote < this.selectedTier.floor ||
          this.draft.cardQuote < this.selectedMaterial.floor ||
          this.draft.excessQuote < this.selectedProduct.excess.floor ||
          this.draft.stopQuote < this.selectedProduct.stop.floor
      }
      return this.draft.unitQuote < this.simpleUnitPrice.floor
    },
    pushLine(belowFloor) {
      const quantity = this.draft.quantity
      let goodsAmount = 0
      let quoteText = ''
      let summary = ''
      if (this.draft.productType === 'sim' && this.draft.packageKind !== 'common') {
        goodsAmount = Number(this.draft.packageQuote) * quantity
        quoteText = `套餐 ${this.money(this.draft.packageQuote)}`
        summary = this.catalogSummary()
      } else if (this.draft.productType === 'sim') {
        goodsAmount = (Number(this.draft.packageQuote) + Number(this.draft.cardQuote)) * quantity
        quoteText = `套餐 ${this.money(this.draft.packageQuote)} / 卡板 ${this.money(this.draft.cardQuote)}`
        const voice = this.selectedProduct.voice ? ` / ${this.selectedProduct.voice}` : ''
        summary = `SIM卡 ${this.draft.carrier} / ${this.draft.scene} / ${this.draft.size}${voice} / ${this.selectedMaterial.name} / ${this.draft.shape} / ${this.draft.orderCycles}个周期`
      } else {
        goodsAmount = Number(this.draft.unitQuote) * quantity
        quoteText = this.money(this.draft.unitQuote)
        summary = this.lineSummary()
      }
      this.lines.push({
        id: this.nextLineId++,
        summary,
        quantity,
        quoteText,
        goodsAmount,
        goodsAmountText: this.money(goodsAmount),
        belowFloor,
        stockText: this.stock.tracked ? `${this.stock.count}${this.quantityUnit}` : '不核库存',
        customerText: this.customerLineText(summary, quantity, goodsAmount)
      })
      this.keepSharedAndReset()
      this.$message.success('已添加本行，可更换产品类型继续添加')
    },
    catalogSummary() {
      const cycles = `${this.draft.orderCycles}个周期`
      if (this.draft.packageKind === 'pool') {
        const poolType = this.draft.poolType === 'multi' ? '融合运营商' : '单一运营商'
        return `流量总池包 ${poolType} / ${this.draft.size} / ${cycles}`
      }
      const addonType = this.draft.addonType === 'voice' ? '语音加油包' : '流量加油包'
      return `${addonType} ${this.draft.size} / ${cycles}`
    },
    lineSummary() {
      if (this.draft.productType === 'esim') {
        return `eSIM ${this.currentGoods.name} / ${this.draft.material} / ${this.draft.shape}`
      }
      if (this.draft.productType === 'profile') {
        return `Profile ${this.currentGoods.name}`
      }
      if (this.draft.productType === 'module') {
        return `模组 ${this.currentGoods.producer} / ${this.currentGoods.model}`
      }
      return `CPE ${this.currentGoods.model} / ${this.currentGoods.comm}`
    },
    customerLineText(summary, quantity, goodsAmount) {
      if (this.draft.productType === 'sim' && this.draft.packageKind !== 'common') {
        return [
          summary,
          `数量 ${quantity}个`,
          `套餐费 ${this.money(this.draft.packageQuote)}元`,
          `小计 ${this.money(goodsAmount)}元`
        ].join('\n')
      }
      if (this.draft.productType === 'sim') {
        return [
          summary,
          `数量 ${quantity}张`,
          `套餐费 ${this.money(this.draft.packageQuote)}元`,
          `卡板费 ${this.money(this.draft.cardQuote)}元`,
          `超额资费 ${this.money(this.draft.excessQuote)}${this.selectedProduct.excess.unit}`,
          `停机保号 ${this.money(this.draft.stopQuote)}${this.selectedProduct.stop.unit}`,
          `小计 ${this.money(goodsAmount)}元`
        ].join('\n')
      }
      return [
        summary,
        `数量 ${quantity}${this.quantityUnit}`,
        `单价 ${this.money(this.draft.unitQuote)}元`,
        `小计 ${this.money(goodsAmount)}元`
      ].join('\n')
    },
    keepSharedAndReset() {
      const productType = this.draft.productType
      const packageKind = this.draft.packageKind
      const poolType = this.draft.poolType
      const addonType = this.draft.addonType
      const carrier = this.draft.carrier
      const scene = this.draft.scene
      const producer = this.draft.producer
      this.draft = emptyDraft(productType)
      this.draft.packageKind = packageKind
      if (productType === 'sim' && packageKind === 'common') {
        this.draft.carrier = carrier
        this.draft.scene = scene
      }
      if (productType === 'sim' && packageKind === 'pool') {
        this.draft.poolType = poolType
      }
      if (productType === 'sim' && packageKind === 'addon') {
        this.draft.addonType = addonType
      }
      if (productType === 'module') {
        this.draft.producer = producer
      }
    },
    removeLine(id) {
      this.lines = this.lines.filter(item => item.id !== id)
    },
    copyCustomerQuote() {
      const lines = this.lines.map((item, index) => `${index + 1}. ${item.customerText}`).join('\n\n')
      const customer = this.quote.customerName ? `客户：${this.quote.customerName}\n` : ''
      const text = `销售报价\n${customer}有效期：${this.quote.validDays}天\n\n${lines}\n\n合计 ${this.money(this.goodsTotal)}元`
      this.$copyText(text).then(() => {
        this.$message.success('已复制客户可见报价')
      }).catch(() => {
        this.$message.error('复制失败')
      })
    },
    saveQuote() {
      this.$message.success('假数据已留在本页，未写入服务器')
    }
  }
}
</script>

<style lang="less" scoped>
.quote-section {
  margin-bottom: 8px;
}

.section-hint {
  margin: -8px 0 16px;
}

.section-title {
  margin-bottom: 16px;
  font-weight: 500;
}

.unit-text {
  margin-left: 8px;
}

.hint {
  color: rgba(0, 0, 0, 0.45);
  line-height: 22px;
}

.stock-box {
  margin-top: 16px;
}

.add-line-action {
  margin-top: 16px;
  text-align: right;
}

.quote-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
}

.card-select-box {
  margin: 0 10px 8px 0;
  border-radius: 0 !important;
  border: 1px solid #d9d9d9;
  text-align: center;

  &.card-type-box {
    min-width: 96px;
  }

  &.card-shape-box {
    min-width: 72px;
  }
}
</style>
